import { useActor } from '@xstate/react';
import type { InterpreterFrom } from 'xstate';
import { useNavigate } from 'react-router-dom';
import { Loader } from '@hotjar/hotsauce';
import { SUBSCRIPTIONS, PAYMENT_DETAILS } from '~/subscription-management/constants/routes';
import type { checkoutMachine } from '../../state-machine/checkout.machine';
import * as S from './ConfirmOrderContainer.styles';
import { Reactivation } from './components/reactivation/Reactivation';
import { useServiceResourceList } from '~/subscription-management/features/service-resource-list';
import { Overdue } from './components/overdue/Overdue';

interface Props {
  checkoutService: InterpreterFrom<typeof checkoutMachine>;
}

export function ConfirmOrderContainer({ checkoutService }: Props): JSX.Element {
  const [state, send] = useActor(checkoutService);
  const navigate = useNavigate();

  const {
    billingPeriod,
    allowancesToAdd,
    allEffectiveAllowances,
    allRenewedAllowances,
    allowancesToRemove,
    userId,
    accountId,
    customer: { currency, currentPeriodCostSummary, billingAddress },
    subscription,
    costs,
  } = state.context;

  const { allResources, loading } = useServiceResourceList({
    userId,
    accountId,
    productAllowances: {
      effectiveNow: allEffectiveAllowances,
      renewWith: allRenewedAllowances,
    },
    billingPeriod,
    currency,
    costs: {
      current: currentPeriodCostSummary,
      future: costs?.renewWith ?? currentPeriodCostSummary,
    },
  });

  return (
    <S.Container aria-live="assertive">
      {state.matches({ confirmOrder: 'reactivation' }) && (
        <Loader isLoading={loading}>
          <Reactivation
            onSubmit={() => send('NEXT')}
            onCancel={() => navigate(SUBSCRIPTIONS)}
            currency={currency}
            updatedProducts={allowancesToAdd}
            allResources={allResources}
            billingPeriod={billingPeriod}
          />
        </Loader>
      )}

      {state.matches({ confirmOrder: 'overdue' }) && (
        <Overdue
          onSubmit={() => navigate(PAYMENT_DETAILS)}
          onCancel={() => send('NEXT')}
          allowancesToRemove={allowancesToRemove}
          accountId={accountId}
          currentPeriodEndDate={subscription?.currentPeriodEndDate}
          currentPeriodCostSummary={currentPeriodCostSummary}
          country={billingAddress?.country}
          billingPeriod={billingPeriod}
          currency={currency}
        />
      )}
    </S.Container>
  );
}
